/**
 * CadenceBlock — the Schedule step's recurrence controls: whether
 * the workshop repeats, how the series ends, and a live preview of the dates.
 * The weekday comes from the Date field above, so there is nothing to pick
 * twice: a Thursday repeats on Thursdays.
 */
import { TWO_COLUMN_GRID } from '../../primitives/two-column-grid';
import { Field, fieldStyle } from '../../venues/venueCreateFields';

import { SeriesEndField } from './SeriesEndField';
import { SeriesPreview } from './SeriesPreview';
import type { WorkshopCreateForm } from './useWorkshopCreateForm';
import { REPEAT_OPTIONS } from './workshopCreateSeries';

const COL: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 14 };

export function CadenceBlock({ form }: { form: WorkshopCreateForm }) {
  const { draft, editSchedule, fieldError, seriesPreview, minDate } = form;
  const s = draft.schedule;
  // While the live preview is explaining why the series is refused, the field
  // stays quiet; it speaks only when the preview has nothing to say, which is
  // when the message came back from the API.
  const fieldMessage = seriesPreview?.problem ? undefined : fieldError('recurrence');
  return (
    <div style={COL}>
      <Field label="Repeats" error={fieldMessage}>
        <select
          aria-label="Repeats"
          className="nb-dt-input"
          style={fieldStyle(fieldMessage ? 'error' : undefined)}
          value={s.repeat}
          onChange={(e) => editSchedule('repeat', e.target.value)}
        >
          {REPEAT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </Field>
      {s.repeat !== 'none' && (
        <>
          <div style={TWO_COLUMN_GRID}>
            <Field label="Ends" required>
              <select
                aria-label="Ends"
                className="nb-dt-input"
                style={fieldStyle(undefined)}
                value={s.endMode}
                onChange={(e) => editSchedule('endMode', e.target.value)}
              >
                <option value="until">On a date</option>
                <option value="count">After a number of workshops</option>
              </select>
            </Field>
            <SeriesEndField form={form} />
          </div>
          <SeriesPreview
            preview={seriesPreview}
            minDate={minDate}
            error={fieldError('seriesDates') ?? fieldError('occurrenceStarts')}
            onEdit={form.editOccurrence}
            onReset={form.resetOccurrence}
          />
        </>
      )}
    </div>
  );
}
